import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { ClarityAccountCardView } from '@src/components/sco/Advisors/clarityLink/ClarityAccountCardView';
import { CLARITY_LINKED_APP, clarityLinkErrorMessage, parseClarityHandleInput } from '@src/components/sco/Advisors/clarityLink/clarityLinkHelpers';
import { clarityReviewSyncSummary } from '@src/components/sco/Advisors/reviews/sessionReviewHelpers';
import { CLAIM_CLARITY_ADVISOR_IMPORT } from '@src/gql/mutations/ClaimClarityAdvisorImport';
import { REQUEST_CLARITY_LINK_CODE } from '@src/gql/mutations/ClarityLink';
import { SYNC_CLARITY_REVIEWS } from '@src/gql/mutations/SessionReviewMutations';
import { CLARITY_ADVISOR_IMPORT } from '@src/gql/queries/ClarityAdvisorImport';
import { OWN_SESSION_REVIEW_SUMMARY } from '@src/gql/queries/SessionReviews';
import { Mutation, Query, ScoClarityAdvisorImportClaimStatus, ScoClarityLinkProofMethod } from '@src/graphql-types';
import { useState } from 'react';
type ClaimPayload = Mutation['claimClarityAdvisorImport'];
const linkedNotice = (payload?: ClaimPayload | null) => {
  if (payload?.reviewSyncError) {
    return 'Clarity account linked. Your reviews could not be synced yet. Use Sync from Clarity to try again.';
  }
  return payload?.reviewSync ? `Clarity account linked. ${clarityReviewSyncSummary(payload.reviewSync)}` : 'Clarity account linked.';
};

/** Advisor profile › Settings › Clarity.fm account (Clarity link plan D1–D2). */
export const ClarityAccountSection = () => {
  const own = useQuery<Pick<Query, 'currentUser'>>(OWN_SESSION_REVIEW_SUMMARY);
  const clarityLink = (own.data?.currentUser?.linkedAccounts ?? []).find(account => account?.app === CLARITY_LINKED_APP);
  const linkedMemberId = clarityLink?.externalUserId || null;

  // Linked: who it is. Clarity being down just leaves the name out.
  const linkedPreview = useQuery<Pick<Query, 'clarityAdvisorImport'>, {
    handle: string;
  }>(CLARITY_ADVISOR_IMPORT, {
    skip: !linkedMemberId,
    variables: {
      handle: linkedMemberId || ''
    }
  });
  const [handleInput, setHandleInput] = useState('');
  const [lookupHandle, setLookupHandle] = useState('');
  const [findPreview, found] = useLazyQuery<Pick<Query, 'clarityAdvisorImport'>, {
    handle: string;
  }>(CLARITY_ADVISOR_IMPORT, {
    fetchPolicy: 'network-only'
  });
  const [requestCode, requestCodeState] = useMutation<Pick<Mutation, 'requestClarityLinkCode'>>(REQUEST_CLARITY_LINK_CODE);
  const [claim, claimState] = useMutation<Pick<Mutation, 'claimClarityAdvisorImport'>>(CLAIM_CLARITY_ADVISOR_IMPORT);
  const [sync, syncState] = useMutation<Pick<Mutation, 'syncClarityReviews'>>(SYNC_CLARITY_REVIEWS);
  const [codeSent, setCodeSent] = useState<{
    maskedEmail: string | null;
  } | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const candidate = lookupHandle ? found.data?.clarityAdvisorImport || null : null;
  const claimHandle = candidate?.canonicalScreenName || lookupHandle;
  const reset = () => {
    setCodeSent(null);
    setCode('');
    setError(null);
    setNotice(null);
  };
  const onFind = async () => {
    const handle = parseClarityHandleInput(handleInput);
    reset();
    setLookupHandle(handle);
    if (!handle) {
      setError('Enter your Clarity username or profile link.');
      return;
    }
    try {
      await findPreview({
        variables: {
          handle
        }
      });
    } catch (err) {
      setError(clarityLinkErrorMessage(err, 'Could not look up that Clarity account.'));
    }
  };
  const finishClaim = async (withCode: string | null) => {
    setError(null);
    try {
      const result = await claim({
        variables: {
          input: {
            handle: claimHandle,
            confirm: true,
            code: withCode
          }
        }
      });
      await own.refetch();
      setCodeSent(null);
      setCode('');
      setLookupHandle('');
      setHandleInput('');
      setNotice(linkedNotice(result.data?.claimClarityAdvisorImport));
    } catch (err_0) {
      setError(clarityLinkErrorMessage(err_0, 'Could not link that Clarity account.'));
    }
  };
  const sendCode = async () => {
    setError(null);
    try {
      const result_0 = await requestCode({
        variables: {
          input: {
            handle: claimHandle
          }
        }
      });
      const payload = result_0.data?.requestClarityLinkCode;
      if (payload?.proofMethod === ScoClarityLinkProofMethod.EmailMatch) {
        // The emails match (or it's already linked to this account): no code needed.
        await finishClaim(null);
        return;
      }
      setCode('');
      setCodeSent({
        maskedEmail: payload?.maskedEmail || null
      });
    } catch (err_1) {
      setError(clarityLinkErrorMessage(err_1, 'Could not send a code.'));
    }
  };
  const onLink = () => candidate?.proofMethod === ScoClarityLinkProofMethod.EmailCode ? sendCode() : finishClaim(null);
  const onSync = async () => {
    setError(null);
    setNotice(null);
    try {
      const result_1 = await sync({
        variables: {
          input: {}
        }
      });
      setNotice(clarityReviewSyncSummary(result_1.data?.syncClarityReviews?.sync));
    } catch (err_2) {
      setError(clarityLinkErrorMessage(err_2, 'Clarity sync failed.'));
    }
  };
  const linkedExpert = linkedPreview.data?.clarityAdvisorImport?.expert || null;
  return <ClarityAccountCardView error={error} finding={found.loading} handleInput={handleInput} linked={linkedMemberId ? {
    expert: linkedExpert
  } : null} linking={claimState.loading || requestCodeState.loading} loading={own.loading && !own.data} notice={notice} syncing={syncState.loading} candidate={candidate ? {
    claimStatus: candidate.claimStatus || ScoClarityAdvisorImportClaimStatus.NotFound,
    proofMethod: candidate.proofMethod,
    expert: candidate.expert
  } : null} codeStep={codeSent ? {
    maskedEmail: codeSent.maskedEmail,
    code,
    onCodeChange: setCode,
    onVerify: () => void finishClaim(code),
    onResend: () => void sendCode(),
    verifying: claimState.loading,
    resending: requestCodeState.loading
  } : null} onFind={() => void onFind()} onHandleInputChange={setHandleInput} onLink={() => void onLink()} onSync={() => void onSync()} />;
};